/* The console's half of the persisted query cache.
 *
 * `@sx/api-client` owns the rules — build id busts the snapshot, age expires it, one
 * principal owns it, and a read may opt out. What is here is what only this application
 * knows: which slot the bytes live in (`browser-storage.ts`, the one boundary to
 * browser-owned persistence, which also reports a refused write), which build wrote them
 * (`__BUILD_ID__`, baked by `vite.config.ts`), and who the session resolved to.
 *
 * Called from `main.tsx` before the first render, and from nowhere else — the suites build
 * a router per case and must not inherit a previous case's snapshot.
 */

import {
  noPersistedQueryCache,
  persistQueryCache,
  type PersistedQueryCache,
} from "@sx/api-client";
import type { QueryClient } from "@tanstack/react-query";

import { browserStorageSlot } from "./browser-storage";
import { loadedSession } from "./session";

/** Device-scoped, because at restore time nobody knows yet whose cache this is: the
 * snapshot names its own principal and is discarded if that turns out to be someone
 * else. */
const SLOT = { kind: "device" } as const;
const SLOT_NAME = "query-cache";

/** A day. Longer than any shift, short enough that an operator opening the console on
 * Monday is not shown Friday's fleet. */
const MAX_AGE_MS = 24 * 60 * 60_000;

/** Restore the last snapshot into `queryClient` and keep writing it back.
 *
 * Identity is deliberately *not* in it (`sessionQuery` carries `NOT_PERSISTED`), so the
 * console never opens as whoever used this device last. What the snapshot buys is
 * everything below identity: once the session read confirms the person — one door call —
 * the portfolio, the workspace grants and each page's last answer are already in memory,
 * so `Region`'s retention path paints them and the revalidation happens behind the paint
 * instead of in front of a skeleton.
 *
 * `_console`'s `beforeLoad` calls `own` with that confirmed principal, and a snapshot that
 * belonged to anybody else — or to somebody, when the session says signed out — is
 * dropped before a single route reads from it. A cleared cache is written through
 * immediately, so nothing outlives the sign-out that emptied it. */
export function persistConsoleQueryCache(
  queryClient: QueryClient,
): PersistedQueryCache {
  kept = persistQueryCache({
    queryClient,
    slot: browserStorageSlot(SLOT, SLOT_NAME),
    buildId: __BUILD_ID__,
    maxAgeMs: MAX_AGE_MS,
    principalId: () => loadedSession(queryClient)?.principal.id,
  });
  // The snapshot is written a second behind the cache. A page left inside that second —
  // a reload, or an address opened right after a save — restored the answer from before
  // the save, and young enough by `staleTime` that nothing revalidated it: the project
  // just created was missing from the list on the next load. So the page writes on its
  // way out. After sign-out `kept` is the do-nothing cache, so this cannot write back.
  window.addEventListener("pagehide", () => kept.save());
  return kept;
}

/* One browser page keeps one snapshot, so the snapshot is remembered here rather than
 * threaded through the router: two of its three callers are not routes. `main.tsx` starts
 * it, `_console`'s `beforeLoad` attributes it, and sign-out ends it. Until something
 * starts it — every suite that builds a router of its own — this is the total do-nothing
 * cache, so neither of the other two callers has to ask whether persistence is on. */
let kept: PersistedQueryCache = noPersistedQueryCache;

/** The snapshot this page restored, for the two callers that are not the one that made
 * it: the route that says whose it is, and the sign-out that ends it. */
export const consoleQueryCache = (): PersistedQueryCache => kept;

/** Stop keeping the cache. Sign-out's last act before the browser leaves: `clear()` on
 * its own is not enough, because this page lives on until the logout POST is followed and
 * every mounted observer refetches in that window. */
export function forgetConsoleQueryCache(): void {
  kept.stop();
  kept = noPersistedQueryCache;
}
